import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { HALResponse } from "ng/src/app/shared/model/app.model";
import { SystemNotification, SystemNotificationAttention, SystemNotificationAttentionId, SystemNotificationRelevance, SystemNotificationRelevanceId } from "ng/src/app/notifications/notification.model";
import { merge, Observable, of, Subject, timer } from "rxjs";
import { catchError, filter, map, share, shareReplay, switchMap, tap } from "rxjs/operators";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { UserService } from "ng/src/app/users/shared/user.service";
import { BroadcastChannelTypes, SystemNotificationChannelEvent } from "ng/src/app/shared/broadcast-channels/broadcast-channels.model";
import { User } from "ng/src/app/users/shared/user.model";
import { BroadcastChannelEntry, getBroadcastChannel } from "ng/src/app/shared/broadcast-channels/broadcast-channels";

@Injectable({
	providedIn: 'root'
})
export class SystemNotificationService {
	
	private lastNotificationsReceivedPoll?: Date;
	private lastSeenPolledNotifications?: ReadonlyArray<SystemNotification>;
	private channelNotificationsSubject = new Subject<ReadonlyArray<SystemNotification>>();
	
	private channelEntry: BroadcastChannelEntry<BroadcastChannelTypes.SYSTEM_NOTIFICATION>;
	private attentions?: SystemNotificationAttention[];
	private relevances?: SystemNotificationRelevance[];
	private _unreadCount = 0;
	
	private poll$?: Observable<ReadonlyArray<SystemNotification> | Error | HttpErrorResponse>;
	
	constructor(
		private http: HttpClient,
		private userService: UserService
	) {
		this.channelEntry = getBroadcastChannel(BroadcastChannelTypes.SYSTEM_NOTIFICATION);
		this.channelEntry.observe(SystemNotificationChannelEvent.USER_UPDATED)
			.subscribe(ev => {
				if (AppStateService.currentUser) {
					if ('dateOfLastViewedSystemNotification' in ev.value) {
						AppStateService.currentUser.dateOfLastViewedSystemNotification = ev.value.dateOfLastViewedSystemNotification;
					}
					if ('dateOfLastDeliveredSystemNotification' in ev.value) {
						AppStateService.currentUser.dateOfLastDeliveredSystemNotification = ev.value.dateOfLastDeliveredSystemNotification;
					}
					
					this.updateUnreadCount();
				}
			});
		
		this.channelEntry.observe(SystemNotificationChannelEvent.RECEIVED_NOTIFICATIONS)
			.subscribe(ev => {
				this.lastNotificationsReceivedPoll = new Date();
				this.lastSeenPolledNotifications = Object.freeze(ev.value);
				this.channelNotificationsSubject.next(this.lastSeenPolledNotifications);
				this.updateUnreadCount();
			});
	}
	
	getNotifications(page: number = 0) {
		return this.http.get<HALResponse<{ systemNotifications: SystemNotification[] }>>('/api/systemNotifications', {
			params: {
				sort: 'created,desc',
				page
			}
		})
	}
	
	getById(id: number): Observable<SystemNotification | undefined> {
		return this.http.get<HALResponse<{ systemNotifications: SystemNotification[] }>>('/api/systemNotifications', {
			params: {
				id
			}
		})
			.pipe(
				map(res => res._embedded.systemNotifications[0])
			);
	}
	
	post({title, content, attention, relevance, endTime, sendAsEmail}: SystemNotificationModel) {
		const toPost = {
			title,
			content,
			attention: `/api/systemNotificationAttentions/${attention}`,
			relevance: `/api/systemNotificationRelevances/${relevance}`,
			endTime,
			sendAsEmail
		}
		
		return this.http.post('/api/systemNotifications', toPost);
	}
	
	deleteById(id: number) {
		return this.http.delete(`/api/systemNotifications/${id}`);
	}
	
	getSystemNotificationEmailStats(ids: number[]) {
		return this.http.get<SystemNotificationEmailStats[]>('/api/systemNotificationDetails/emailStats', {
			params: {
				id: ids
			}
		});
	}
	
	getEmailBatchSize() {
		return this.http.get<number>('/api/systemNotificationDetails/batchSize');
	}
	
	setEmailBatchSize(batchSize: number) {
		return this.http.post('/api/systemNotificationDetails/batchSize', batchSize);
	}
	
	getAddresses(...notificationId: number[]) {
		return this.http.get<Record<number, string[]>>('/api/systemNotificationDetails/addresses', {
			params: {
				id: notificationId
			}
		});
	}
	
	getAttentions() {
		if (Array.isArray(this.attentions)) {
			return of([...this.attentions]);
		}
		
		return this.http.get<HALResponse<{ systemNotificationAttentions: SystemNotificationAttention[] }>>('/api/systemNotificationAttentions')
			.pipe(
				map((res) => this.attentions ??= [...res._embedded.systemNotificationAttentions])
			)
	}
	
	getRelevances() {
		if (Array.isArray(this.relevances)) {
			return of([...this.relevances]);
		}
		
		return this.http.get<HALResponse<{ systemNotificationRelevances: SystemNotificationRelevance[] }>>('/api/systemNotificationRelevances')
			.pipe(
				map((res) => this.relevances ??= [...res._embedded.systemNotificationRelevances])
			)
	}
	
	getLastViewedTimestamp() {
		const dateStr = AppStateService.currentUser?.dateOfLastViewedSystemNotification;
		
		if (!dateStr)
			return undefined;
		
		return new Date(dateStr);
	}
	
	getLastDeliveredTimestamp() {
		const dateStr = AppStateService.currentUser?.dateOfLastDeliveredSystemNotification;
		
		if (!dateStr)
			return undefined;
		
		return new Date(dateStr);
	}
	
	getUnreadCount() {
		return this._unreadCount;
	}
	
	updateLastSeen(notification: SystemNotification) {
		const currentUser = AppStateService.currentUser;
		if (!currentUser) {
			throw new Error('undefined currentUser while updating system notification details: seen');
		}
		
		const currentLastViewed = this.getLastViewedTimestamp();
		if (currentLastViewed && currentLastViewed >= new Date(notification.created)) {
			return of(currentLastViewed.toISOString());
		}
		
		return this.userService.updateLastSeenSystemNotificationDate(currentUser.id, 'dateOfLastViewedSystemNotification',notification.created)
			.pipe(
				tap((s) => this.updateChannelUser({ dateOfLastViewedSystemNotification: s }))
			);
	}
	
	updateLastDelivered(notification: SystemNotification) {
		const currentUser = AppStateService.currentUser;
		if (!currentUser) {
			throw new Error('undefined currentUser while updating system notification details: delivered');
		}
		
		const currentLastDelivered = this.getLastViewedTimestamp();
		if (currentLastDelivered && currentLastDelivered >= new Date(notification.created)) {
			return of(currentLastDelivered.toISOString());
		}
		
		return this.userService.updateLastSeenSystemNotificationDate(currentUser.id, 'dateOfLastDeliveredSystemNotification', notification.created)
			.pipe(
				tap((s) => this.updateChannelUser({ dateOfLastDeliveredSystemNotification: s }))
			);
	}
	
	getPoll() {
		let timePeriod = 5 * 60 * 1000;
		let userUpdated: Date | undefined = undefined;
		let lastStartedTime: number | undefined = undefined;
		
		const pollOb$ = merge(
			timer(0, timePeriod).pipe(
				filter(() => {
					if (!this.lastNotificationsReceivedPoll)
						return true;
					
					lastStartedTime = this.lastNotificationsReceivedPoll.getTime();
					const cmpDate = new Date(lastStartedTime + timePeriod - Math.floor(timePeriod * 0.25));
					return cmpDate < new Date();
				})
			),
			AppStateService.currentUser$.pipe(
				filter(user => {
					if (!user) return false;
					if (!userUpdated) return true;
					
					return new Date(user.updated) > userUpdated;
				}),
				tap(user => {
					userUpdated = new Date(user!.updated);
				})
			)
		).pipe(
			switchMap(() => {
				return this.getNotifications().pipe(
					map(res => {
						const frozen = Object.freeze(res._embedded.systemNotifications);
						this.lastSeenPolledNotifications = frozen;
						this.updateUnreadCount();
						
						this.channelEntry.postEvent(SystemNotificationChannelEvent.RECEIVED_NOTIFICATIONS, res._embedded.systemNotifications);
						
						return frozen as ReadonlyArray<SystemNotification>;
					}),
					filter(() => {
						return lastStartedTime === this.lastNotificationsReceivedPoll?.getTime();
					}),
					tap(() => {
						this.lastNotificationsReceivedPoll = new Date();
					}),
					catchError((e: HttpErrorResponse | Error) => of(e))
				)
			}),
			share()
		);
		
		return this.poll$ ??= merge(
			this.channelNotificationsSubject,
			pollOb$
		)
			.pipe(
				shareReplay(1)
			)
	}
	
	private updateUnreadCount() {
		const date = this.getLastViewedTimestamp();
		if (!this.lastSeenPolledNotifications) {
			this._unreadCount = 0;
			return;
		}
		
		if (!date) {
			this._unreadCount = this.lastSeenPolledNotifications.length;
		}
		else {
			this._unreadCount = this.lastSeenPolledNotifications.filter(n => new Date(n.created) > date).length;
		}
	}
	
	private updateChannelUser(update: Pick<User, 'dateOfLastDeliveredSystemNotification' | 'dateOfLastViewedSystemNotification'>) {
		this.channelEntry.postEvent(SystemNotificationChannelEvent.USER_UPDATED, update);
	}
}

export interface SystemNotificationModel {
	title: string,
	content: string,
	attention: SystemNotificationAttentionId,
	relevance: SystemNotificationRelevanceId,
	endTime?: Date,
	sendAsEmail: boolean
}

export interface SystemNotificationEmailStats { notificationId: number, delivered: number, count: number }